import { format as dateFnsFormat, parseISO } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

/**
 * Date/Time Formatting Utilities
 * Respects platform configuration for formats and timezone
 */

/**
 * Convert date format string from config to date-fns format
 */
function convertDateFormat(configFormat: string): string {
  const formatMap: Record<string, string> = {
    'MM/DD/YYYY': 'MM/dd/yyyy',
    'DD/MM/YYYY': 'dd/MM/yyyy',
    'YYYY-MM-DD': 'yyyy-MM-dd',
    'YYYY/MM/DD': 'yyyy/MM/dd',
  };
  
  return formatMap[configFormat] || 'MM/dd/yyyy';
}

/**
 * Get time format string for date-fns
 */
function getTimeFormat(is24Hour: boolean): string {
  return is24Hour ? 'HH:mm' : 'h:mm a';
}

/**
 * Format a date according to platform configuration
 * @param date - Date to format (Date object, string, or timestamp)
 * @param dateFormat - Optional override for date format
 * @param timezone - Optional override for timezone (e.g., 'America/New_York')
 */
export function formatDate(
  date: Date | string | number | null | undefined,
  dateFormat?: string,
  timezone?: string
): string {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
    const format = convertDateFormat(dateFormat || 'MM/DD/YYYY');
    
    if (timezone) {
      return formatInTimeZone(dateObj, timezone, format);
    }
    
    return dateFnsFormat(dateObj, format);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

/**
 * Format a time according to platform configuration
 * @param date - Date to format (Date object, string, or timestamp)
 * @param is24Hour - Whether to use 24-hour format
 * @param timezone - Optional override for timezone
 */
export function formatTime(
  date: Date | string | number | null | undefined,
  is24Hour = false,
  timezone?: string
): string {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
    const format = getTimeFormat(is24Hour);
    
    if (timezone) {
      return formatInTimeZone(dateObj, timezone, format);
    }
    
    return dateFnsFormat(dateObj, format);
  } catch (error) {
    console.error('Error formatting time:', error);
    return '';
  }
}

/**
 * Format both date and time according to platform configuration
 * @param date - Date to format (Date object, string, or timestamp)
 * @param dateFormat - Optional override for date format
 * @param is24Hour - Whether to use 24-hour format
 * @param timezone - Optional override for timezone
 */
export function formatDateTime(
  date: Date | string | number | null | undefined,
  dateFormat?: string,
  is24Hour = false,
  timezone?: string
): string {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
    const datePart = convertDateFormat(dateFormat || 'MM/DD/YYYY');
    const timePart = getTimeFormat(is24Hour);
    const format = `${datePart} ${timePart}`;
    
    if (timezone) {
      return formatInTimeZone(dateObj, timezone, format);
    }
    
    return dateFnsFormat(dateObj, format);
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return '';
  }
}

/**
 * Format date relative to now (e.g., "2 hours ago", "in 3 days")
 */
export function formatRelativeTime(date: Date | string | number | null | undefined): string {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
    if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
    if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
    if (diffDay < 30) {
      const weeks = Math.floor(diffDay / 7);
      return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
    }
    if (diffDay < 365) {
      const months = Math.floor(diffDay / 30);
      return `${months} month${months !== 1 ? 's' : ''} ago`;
    }
    const years = Math.floor(diffDay / 365);
    return `${years} year${years !== 1 ? 's' : ''} ago`;
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return '';
  }
}

/**
 * Convert date to user's configured timezone
 */
export function toConfiguredTimezone(
  date: Date | string | number,
  timezone: string
): Date {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
    return toZonedTime(dateObj, timezone);
  } catch (error) {
    console.error('Error converting to timezone:', error);
    return new Date(date);
  }
}
